import type { QueryClient } from '@tanstack/react-query';

/** Paginated lists and every aggregate derived from them. Plural. */
export const FUEL_TRANSACTIONS_QUERY_KEY = 'fuelTransactions';
/**
 * A single transaction, keyed by id. Singular, so it is NOT a prefix match of the plural key —
 * React Query compares key arrays element by element, and `'fuelTransaction' !== 'fuelTransactions'`.
 * Invalidating only the plural key leaves the details and edit drawers serving stale data.
 */
export const FUEL_TRANSACTION_QUERY_KEY = 'fuelTransaction';

// Prefixos das factories de `@cobliteam/api` (`fuelCardQueries`, `conferenceQueries`), repetidos porque este módulo não depende do
// pacote; o teste daqui os compara com as chaves reais. O `fuelCard` sozinho é o domínio inteiro e pegaria também o catálogo de
// parceiros e as integrações da frota, que uma escrita de transação não altera: só duas famílias dele entram.
const FUEL_CARD_QUERY_KEY = 'fuelCard';
const CONFERENCE_QUERY_KEY = 'conference';

const TCO_QUERY_KEY = 'totalCostsOfOwnership';
const TCO_EXPENSES_SEGMENT = 'expenses';
const TCO_VEHICLES_SEGMENT = 'vehicles';

/**
 * The query families a write to a fuel transaction makes stale.
 *
 * Four, not one, and the two that are easy to miss are the reason this list exists:
 *
 * (Mais duas famílias da conferência de cartão combustível, `fuelCard` e `conference`, estão logo abaixo: o status da conferência
 * e a contagem do banner "a revisar" dependem do cadastro da transação, então editar, excluir ou importar os deixa velhos.)
 *
 * - `fuelTransaction` (singular) because deleting a transaction makes the backend RECALCULATE its
 *   neighbour — consumption, cost per km — and save it (`DeleteTransactions.kt`). Open that
 *   neighbour's drawer right after and the cache still holds the pre-delete numbers.
 * - The two TCO families because a fuel transaction is also an expense: it shows up in the Despesas
 *   list and in the per-vehicle cost aggregate. `useBatchImportFuelTransactions` already invalidates
 *   the expenses one; delete and update did not, so deleting from the fuel-card screen left Despesas
 *   listing a transaction that no longer exists.
 *
 * Each entry is a PREFIX: `[FUEL_TRANSACTIONS_QUERY_KEY]` covers `[key, input]`, `[key, 'summary',
 * input]` and the other six aggregates without naming them.
 */
export const FUEL_TRANSACTION_WRITE_INVALIDATION_KEYS: readonly (readonly string[])[] = [
  [FUEL_TRANSACTIONS_QUERY_KEY],
  [FUEL_TRANSACTION_QUERY_KEY],
  [FUEL_CARD_QUERY_KEY, 'needs-review-count'],
  [FUEL_CARD_QUERY_KEY, 'transaction-photos'],
  [CONFERENCE_QUERY_KEY],
  [TCO_QUERY_KEY, TCO_EXPENSES_SEGMENT],
  [TCO_QUERY_KEY, TCO_VEHICLES_SEGMENT],
];

/** Invalidates every family a fuel-transaction write makes stale. Use on create, update and delete. */
export const invalidateFuelTransactionQueries = (queryClient: QueryClient): void => {
  for (const queryKey of FUEL_TRANSACTION_WRITE_INVALIDATION_KEYS) {
    queryClient.invalidateQueries({ queryKey: [...queryKey] });
  }
};
